/**
 * 实体指标（`GET /v1/metrics/entities`）的游标分页加载。
 *
 * 与 `useEntities` 的分工：本 hook 是**广告指标表的唯一数据源**——它返回的行已经同时带
 * 台账字段（状态 / 预算 / 币种）与两个口径的指标，因此表格不需要再与 `/v1/entities` join
 * （两套排序不同：指标端点是**花费降序**，按 id 升序会让首屏全是零花费的冷实体）。
 *
 * 纪律：
 *   - 切换 level / 窗口 → 丢弃旧行（不同层级/窗口的行不可混）；
 *   - 追加时按 `accountId+id` 去重（游标实现有偏差也不会出现重复行）；
 *   - 卸载/换参数时 abort，避免竞态写入；
 *   - 覆盖率随页返回，整页覆盖率取**最后一页**的 `coverage`（它描述整个窗口，不是某一页）。
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { apiFetchEntityMetrics } from "../lib/apiClient.js";
import type {
  AdEntityLevel,
  EntityMetricsRow,
  ResponseWarning,
  UtmCoverageSummary,
} from "../lib/apiTypes.js";
import type { ResourceState } from "./useApiResource.js";

export interface EntityMetricsResource {
  readonly rows: readonly EntityMetricsRow[];
  readonly state: ResourceState;
  readonly error: unknown;
  readonly hasMore: boolean;
  readonly loadingMore: boolean;
  readonly nextCursor: string | null;
  readonly warnings: readonly ResponseWarning[];
  readonly coverage: UtmCoverageSummary;
  readonly loadMore: () => void;
  readonly reload: () => void;
  /**
   * **写后就地更新**（2026-10-04 第十三批 · 用户第 1 条）：按 (accountId, level, id)
   * 只改刷新命中的行的改动列，**不整表 reload**。
   */
  readonly patchRows: (patches: readonly EntityMetricsRowPatch[]) => void;
}

/** 一条要就地更新的指标行（只含**实际改动**的列；`undefined` = 不改这一列）。 */
export interface EntityMetricsRowPatch {
  readonly accountId: string;
  readonly level: AdEntityLevel;
  readonly id: string;
  readonly status?: string;
  readonly effectiveStatus?: string;
  readonly dailyBudgetCents?: number;
}

/** 行键（与 `loadMore` 的去重口径一致：accountId + id）。 */
function entityMetricsRowKey(row: Pick<EntityMetricsRow, "accountId" | "id">): string {
  return `${row.accountId}\u0000${row.id}`;
}

/**
 * 纯函数：对已加载的行应用补丁。未命中的行**引用不变**（不触发无谓重渲染）；
 * 一个都没命中时返回原数组。只改补丁声明的列。
 */
export function applyEntityMetricsRowPatches(
  rows: readonly EntityMetricsRow[],
  patches: readonly EntityMetricsRowPatch[],
): readonly EntityMetricsRow[] {
  if (patches.length === 0) return rows;
  const index = new Map<string, EntityMetricsRowPatch>();
  for (const patch of patches) index.set(`${patch.accountId}\u0000${patch.id}`, patch);
  let changed = false;
  const next = rows.map((row) => {
    const patch = index.get(entityMetricsRowKey(row));
    if (patch === undefined) return row;
    changed = true;
    return {
      ...row,
      ...(patch.status === undefined ? {} : { status: patch.status }),
      ...(patch.effectiveStatus === undefined ? {} : { effectiveStatus: patch.effectiveStatus }),
      ...(patch.dailyBudgetCents === undefined ? {} : { dailyBudgetCents: patch.dailyBudgetCents }),
    };
  });
  return changed ? next : rows;
}

const EMPTY_COVERAGE: UtmCoverageSummary = {
  shopOrderCount: 0,
  utmMatchedOrderCount: 0,
  utmCoverageRate: null,
};

export function useEntityMetrics(
  level: AdEntityLevel,
  range: { readonly from: string; readonly to: string },
  limit = 200,
  language?: string,
  /**
   * ⭐ 2026-10-07「站点必选」：该页数据**按站点过滤**。
   *  · `undefined` ⇒ 不带该参数（老调用方行为不变）；
   *  · `""` ⇒ **未选站点 ⇒ 不取数**（阻断态由页面渲染「请先选择站点」，不是"没有数据"）；
   *  · 具体站点 ⇒ 请求里**确实带 `shopId`** ⇒ 结果按站点变化。
   */
  shopId?: string,
): EntityMetricsResource {
  const [rows, setRows] = useState<readonly EntityMetricsRow[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [state, setState] = useState<ResourceState>("loading");
  const [error, setError] = useState<unknown>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [warnings, setWarnings] = useState<readonly ResponseWarning[]>([]);
  const [coverage, setCoverage] = useState<UtmCoverageSummary>(EMPTY_COVERAGE);
  const [version, setVersion] = useState(0);
  const requestSeq = useRef(0);
  const { from, to } = range;

  useEffect(() => {
    // ⭐「未选站点 ⇒ 不显示内容」的执行点：**不取数**（比"取了再丢弃"正确）。
    if (shopId === "") {
      setRows([]);
      setState("ready");
      return;
    }

    requestSeq.current += 1;
    const seq = requestSeq.current;
    const controller = new AbortController();

    setState("loading");
    setError(null);
    setRows([]);
    setCursor(null);
    setWarnings([]);
    setCoverage(EMPTY_COVERAGE);

    apiFetchEntityMetrics(
      {
        level,
        from,
        to,
        limit,
        ...(language === undefined ? {} : { language }),
        // 站点必选：把站点真正带进请求（结果按站点变化，不是「加了门仍跨店」）。
        ...(shopId === undefined ? {} : { shopId }),
      },
      controller.signal,
    )
      .then((page) => {
        if (requestSeq.current !== seq) return;
        setRows(page.items);
        setCursor(page.nextCursor);
        setWarnings(page.warnings);
        setCoverage(page.coverage);
        setState("ready");
      })
      .catch((cause: unknown) => {
        if (controller.signal.aborted || requestSeq.current !== seq) return;
        setError(cause);
        setState("error");
      });

    return () => controller.abort();
  }, [level, from, to, limit, language, version, shopId]);

  const loadMore = useCallback(() => {
    if (cursor === null || loadingMore) return;
    setLoadingMore(true);
    const seq = requestSeq.current;
    apiFetchEntityMetrics({ level, from, to, limit, ...(language === undefined ? {} : { language }), cursor })
      .then((page) => {
        if (requestSeq.current !== seq) return;
        setRows((previous) => {
          const seen = new Set(previous.map((row) => `${row.accountId}\u0000${row.id}`));
          const appended = page.items.filter((row) => !seen.has(`${row.accountId}\u0000${row.id}`));
          return [...previous, ...appended];
        });
        setCursor(page.nextCursor);
        setCoverage(page.coverage);
        setWarnings((previous) => (previous.length === 0 ? page.warnings : previous));
      })
      .catch((cause: unknown) => {
        if (requestSeq.current !== seq) return;
        setError(cause);
      })
      .finally(() => {
        if (requestSeq.current === seq) setLoadingMore(false);
      });
  }, [cursor, from, language, level, limit, loadingMore, to]);

  const reload = useCallback(() => setVersion((value) => value + 1), []);

  const patchRows = useCallback((patches: readonly EntityMetricsRowPatch[]): void => {
    if (patches.length === 0) return;
    setRows((previous) => applyEntityMetricsRowPatches(previous, patches));
  }, []);

  return {
    rows,
    state,
    error,
    hasMore: cursor !== null,
    loadingMore,
    nextCursor: cursor,
    warnings,
    coverage,
    loadMore,
    reload,
    patchRows,
  };
}
